import { useEffect, useMemo, useState } from "react";

import { GameModes } from "@Enums/index";
import { useOptionalScenarioRetryContext } from "@Context/game/activity/scenario-retry-context";
import { filterReplayDecisions, getReplayDecisionFilterOptions, type ReplayPassType } from "@hooks/common/session/replay-decision-utils";
import { getSessionResultsScenarioAnalytics } from "@hooks/game/activity/session/session-results-analytics";
import useSessionReplay from "@hooks/game/activity/session/use-session-replay";
import type { SessionResultsData } from "@hooks/game/activity/session/use-session-results.types";
import { useStreamUi } from "@hooks/game/activity/session/use-stream-ui";
import useI18n from "@i18n";

interface IUseSessionFeedbackModel {
  currentTab: number;
  cognitiveResultId: number;
  userId: string;
  sessionResults: SessionResultsData | null;
}

export const useSessionFeedbackModel = ({ currentTab, cognitiveResultId, userId, sessionResults }: IUseSessionFeedbackModel) => {
  const { i18n } = useI18n();

  const original = useSessionReplay({
    currentTab,
    cognitiveResultId,
    userId,
    sessionResults,
  });

  const retry = useOptionalScenarioRetryContext();
  const retryResult = retry?.results[cognitiveResultId];
  const activeRetry = retryResult && retry?.views[cognitiveResultId] ? retryResult : null;
  const showingRetry = Boolean(activeRetry);
  const { replayData, decisions, isUnityReplayReady, areDecisionsLoading, isReplayLoading, isReplayNotFound } = activeRetry
    ? {
        replayData: activeRetry.replayData,
        decisions: activeRetry.decisions,
        isUnityReplayReady: retry?.unityReadyReplays[activeRetry.attemptId] === cognitiveResultId,
        areDecisionsLoading: false,
        isReplayLoading: false,
        isReplayNotFound: false,
      }
    : original;

  const scenarioAnalytics = useMemo(() => getSessionResultsScenarioAnalytics(sessionResults, currentTab, i18n), [currentTab, i18n, sessionResults]);
  const teamStyles = sessionResults?.play_items.find(playItem => playItem.cognitive_result_id === cognitiveResultId)?.replay_team_styles;
  const visibleReplayData = replayData && !areDecisionsLoading ? replayData : null;
  const isMatchPlay = sessionResults?.game_mode === GameModes.MATCH_PLAY;
  const [activeDecisionFilter, setActiveDecisionFilter] = useState<ReplayPassType | null>(null);

  useEffect(() => {
    setActiveDecisionFilter(null);
  }, [cognitiveResultId, currentTab]);

  const decisionFilterOptions = useMemo(() => getReplayDecisionFilterOptions(decisions), [decisions]);
  const visibleDecisions = useMemo(
    () => (isMatchPlay ? filterReplayDecisions(decisions, activeDecisionFilter) : decisions),
    [activeDecisionFilter, decisions, isMatchPlay]
  );

  const { playbackState, playbackUiState, playbackActions } = useStreamUi({
    cognitiveResultId,
    retryAttemptId: activeRetry?.attemptId,
    decisions: visibleDecisions,
    replayData: visibleReplayData,
    isUnityReplayReady,
    scenarioAnalytics,
  });

  return useMemo(
    () => ({
      replay: {
        cognitiveResultId,
        teamStyles,
        replayData: visibleReplayData,
        isUnityReplayReady,
        decisions: visibleDecisions,
        allDecisions: decisions,
        areDecisionsLoading,
        isReplayLoading: isReplayLoading || (Boolean(replayData) && areDecisionsLoading),
        isReplayNotFound,
        decisionFilter: {
          isEnabled: isMatchPlay,
          activeFilter: isMatchPlay ? activeDecisionFilter : null,
          options: decisionFilterOptions,
          setActiveFilter: setActiveDecisionFilter,
        },
      },
      playback: {
        ...playbackState,
        ...playbackUiState,
      },
      actions: playbackActions,
      analytics: {
        scenario: scenarioAnalytics,
      },
      comparison: {
        available: Boolean(retryResult),
        originalDecisions: original.decisions,
        showingRetry,
      },
    }),
    [
      areDecisionsLoading,
      retryResult,
      original.decisions,
      showingRetry,
      cognitiveResultId,
      activeDecisionFilter,
      decisionFilterOptions,
      decisions,
      isMatchPlay,
      isReplayLoading,
      isReplayNotFound,
      playbackActions,
      playbackState,
      playbackUiState,
      replayData,
      isUnityReplayReady,
      scenarioAnalytics,
      teamStyles,
      visibleDecisions,
      visibleReplayData,
    ]
  );
};

export type SessionFeedbackModel = ReturnType<typeof useSessionFeedbackModel>;
